import { UseFormReset, UseFormSetError } from "react-hook-form";
import Form from "../../../forms/Form";
import Checkbox from "../../../forms/inputs/Checkbox";
import useUserDetails from "../../../../utils/hooks/user/useUserDetails";
import { changePassword, setupTOTPApi, enableTOTPApi, disableTOTPApi, requestDisableOTP } from "../../../../utils/api/endpoints/user/user";
import SecondaryButton from "../../../buttons/SecondaryButton";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import ResearcherTextBox from "../../inputs/ResearcherTextBox";

type OTPFieldValues = {
  otpEnabled?: boolean;
};

type PasswordFieldValues = {
  current: string;
  newPassword: string;
  confirmPassword: string;
};

type TOTPSetupState = {
  qrCode: string;
  backupCodes: string[];
  setupToken: string;
};

// Researcher-only Security UI using underline inputs with TOTP support
const ResearcherSecurity = () => {
  const { otpEnabled, setOtpEnabled, fetchDetails, totpSecret } = useUserDetails();
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState<TOTPSetupState | null>(null);
  const [totpVerifyCode, setTotpVerifyCode] = useState("");
  const [isSetupSuccess, setIsSetupSuccess] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const updatePassword = async (
    data: PasswordFieldValues,
    setError: UseFormSetError<PasswordFieldValues>,
    reset: UseFormReset<PasswordFieldValues>
  ) => {
    if (data.confirmPassword !== data.newPassword) {
      setError("confirmPassword", {
        message: "Passwords don't match"
      });
      return;
    }

    try {
      await changePassword(data.current, data.newPassword);
      toast.success("Password changed successfully");
      reset();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Password change failed");
    }
  };

  const handleSetupTOTP = async () => {
    try {
      const data = await setupTOTPApi();
      setSetupData(data);
      setIsSetupSuccess(false);
      setShowSetup(true);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to start TOTP setup");
    }
  };

  const handleVerifySetup = async () => {
    if (!totpVerifyCode || !setupData) return;
    try {
      await enableTOTPApi(totpVerifyCode, setupData.setupToken);
      toast.success("TOTP MFA enabled successfully");
      fetchDetails();
      setIsSetupSuccess(true);
      setTotpVerifyCode("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Verification failed");
    }
  };

  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disable2FACode, setDisable2FACode] = useState("");

  const handleDisableTOTP = async () => {
    if (!disableCode) return;
    try {
      await disableTOTPApi(disableCode);
      toast.success("TOTP MFA disabled successfully");
      fetchDetails();
      setShowDisableDialog(false);
      setDisableCode("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to disable TOTP");
    }
  };

  const update2FA = async (data: OTPFieldValues) => {
    if (otpEnabled && !data.otpEnabled) {
      // Prompt verification modal if user is disabling 2FA
      setShowDisable2FAModal(true);
      if (!totpSecret) {
        try {
          await requestDisableOTP();
          toast.success("Verification code sent to your registered email");
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to send verification code");
        }
      }
    } else {
      setOtpEnabled(!!data.otpEnabled);
    }
  };

  const handleConfirmDisable2FA = async () => {
    if (!disable2FACode) {
      toast.error("Please enter the verification code to proceed");
      return;
    }
    try {
      await setOtpEnabled(false, disable2FACode);
      setShowDisable2FAModal(false);
      setDisable2FACode("");
    } catch (error) {
      // Toast message generated inside hook
    }
  };

  const defaultValues = useMemo(() => ({ otpEnabled }), [otpEnabled]);
  const passwordDefaults = useMemo(
    () => ({ current: "", newPassword: "", confirmPassword: "" }),
    []
  );

  return (
    <div className="text-sm">
      {/* Password section */}
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <Form<PasswordFieldValues>
            defaultValues={passwordDefaults}
            onSubmit={updatePassword}
            className="space-y-4"
          >
            <ResearcherTextBox name="current" placeholder="••••••••" password rules={{ required: true }} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <ResearcherTextBox
                  name="newPassword"
                  placeholder="••••••••"
                  password
                  rules={{
                    required: true,
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                      message: "Weak password"
                    }
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <ResearcherTextBox name="confirmPassword" placeholder="••••••••" password rules={{ required: true }} />
              </div>
            </div>

            <SecondaryButton type="submit" className="mt-2">Change Password</SecondaryButton>
          </Form>
        </div>
      </div>

      {/* 2FA section */}
      <div className="mt-8 border-t border-gray-150 pt-6">
        <h3 className="mb-2 text-base font-semibold text-gray-900">Two-Factor Authentication (Fallback Method)</h3>
        <Form<OTPFieldValues> defaultValues={defaultValues} onSubmit={update2FA} className="space-y-2">
          <div className="flex items-center gap-3">
            <Checkbox name="otpEnabled" label="Enable default email 2FA authentication" className="text-sm" />
          </div>
          <p className="text-xs text-[#6B7280]">
            Requires an alphanumeric OTP sent to your registered email when logging in.
          </p>
          <SecondaryButton type="submit" className="mt-2">Update Fallback 2FA</SecondaryButton>
        </Form>
      </div>

      {/* TOTP Authenticator App section */}
      <div className="mt-8 border-t border-gray-150 pt-6">
        <h3 className="mb-2 text-base font-semibold text-gray-900">TOTP Authenticator Application</h3>
        <p className="text-xs text-[#6B7280] mb-4">
          Use standard apps like Google Authenticator or Microsoft Authenticator to generate secure, offline rotating codes.
        </p>

        {!otpEnabled && !totpSecret ? (
          <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg border-l-4 border-yellow-400 text-xs">
            Please enable the default email 2FA first before configuring an Authenticator app.
          </div>
        ) : (
          <div className="flex gap-4">
            {!totpSecret ? (
              <button onClick={handleSetupTOTP} className="px-4 py-2 bg-[#010D2C] hover:bg-[#010D2C]/90 text-white rounded-lg text-xs font-semibold transition">
                Setup Authenticator App
              </button>
            ) : (
              <button onClick={() => setShowDisableDialog(true)} className="px-4 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition">
                Disable Authenticator App
              </button>
            )}
          </div>
        )}
      </div>

      {/* Setup Modal */}
      {showSetup && setupData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            <button onClick={() => { setShowSetup(false); setSetupData(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Set up Authenticator</h3>
            
            <div className="space-y-4">
              <div className="flex flex-col items-center">
                <img src={setupData.qrCode} alt="TOTP QR Code" className="w-40 h-40 border border-gray-100 rounded-lg mb-2" />
                <p className="text-[10px] text-gray-500 text-center">Scan this QR Code in Google Authenticator or Microsoft Authenticator.</p>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-[10px] font-semibold text-gray-700">Save backup recovery codes (store securely):</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(setupData.backupCodes.join("\n"));
                        toast.success("Recovery codes copied to clipboard!");
                      }}
                      className="text-[10px] text-[#010D2C] hover:underline font-semibold"
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const element = document.createElement("a");
                        const file = new Blob([setupData.backupCodes.join("\n")], { type: 'text/plain' });
                        element.href = URL.createObjectURL(file);
                        element.download = "ctb-mfa-backup-codes.txt";
                        document.body.appendChild(element);
                        element.click();
                        document.body.removeChild(element);
                        toast.success("Codes downloaded!");
                      }}
                      className="text-[10px] text-[#010D2C] hover:underline font-semibold"
                    >
                      Download
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-xs text-[#010D2C]">
                  {setupData.backupCodes.map((code, idx) => (
                    <div key={idx} className="bg-white px-2 py-1 rounded border border-gray-200 text-center">{code}</div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Verify Authenticator Code</label>
                <input
                  type="text"
                  placeholder={isSetupSuccess ? "Verified" : "Enter 6-digit code"}
                  value={isSetupSuccess ? "" : totpVerifyCode}
                  disabled={isSetupSuccess}
                  onChange={(e) => setTotpVerifyCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#010D2C] text-center tracking-widest text-base font-semibold disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <button 
                onClick={handleVerifySetup} 
                disabled={isSetupSuccess}
                className={`w-full py-2 text-white rounded-lg font-semibold text-xs transition ${isSetupSuccess ? "bg-green-600 hover:bg-green-600 cursor-default" : "bg-[#010D2C] hover:bg-[#010D2C]/90"}`}
              >
                {isSetupSuccess ? "Configured ✓" : "Verify and Complete Setup"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable Modal */}
      {showDisableDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            <button onClick={() => setShowDisableDialog(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Disable Authenticator App</h3>
            <p className="text-xs text-gray-500 mb-4">Please confirm by entering your current Authenticator app code or one of your backup recovery codes.</p>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="6-digit code or backup code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-center tracking-widest text-base font-semibold"
              />

              <div className="flex gap-3">
                <button onClick={() => setShowDisableDialog(false)} className="flex-1 py-2 border border-gray-250 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={handleDisableTOTP} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition">
                  Confirm Disable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisable2FAModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative">
            <button onClick={() => { setShowDisable2FAModal(false); setDisable2FACode(""); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Disable Two-Factor Authentication</h3>
            <p className="text-xs text-gray-500 mb-4">
              {totpSecret 
                ? "Please verify your current Authenticator app code or one of your backup recovery codes to confirm disabling two-factor authentication."
                : "Please confirm you wish to disable email fallback two-factor authentication."}
            </p>

            <div className="space-y-4">
              <input
                type="text"
                placeholder={totpSecret ? "6-digit code or backup code" : "4-digit Email OTP"}
                value={disable2FACode}
                onChange={(e) => setDisable2FACode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-center tracking-widest text-base font-semibold"
              />

              <div className="flex gap-3">
                <button onClick={() => { setShowDisable2FAModal(false); setDisable2FACode(""); }} className="flex-1 py-2 border border-gray-250 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={handleConfirmDisable2FA} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition">
                  Confirm Disable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearcherSecurity;
